import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import { Search, Eye, Truck, Package, CheckCircle, XCircle, AlertTriangle, ShoppingBag } from 'lucide-react';
import { adminService } from '../../services/adminService';
import { PageHeader } from '../../components/shared/PageHeader';
import { StatusBadge } from '../../components/shared/StatusBadge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Skeleton } from '../../components/ui/skeleton';
import { EmptyState } from '../../components/shared/EmptyState';
import { Users } from 'lucide-react';
import { toast } from 'sonner';

const StatCard = ({ label, value, icon: Icon, color }) => (
  <div className="bg-card rounded-xl p-5 shadow-card border border-border">
    <div className="flex items-start justify-between">
      <div>
        <p className="text-muted-foreground text-xs font-ui uppercase tracking-wide mb-2">{label}</p>
        <p className="font-serif text-2xl sm:text-3xl font-bold text-foreground">{value}</p>
      </div>
      <div className={`h-10 w-10 rounded-xl flex items-center justify-center ${color}`}>
        <Icon size={20} />
      </div>
    </div>
  </div>
);

export default function AdminOrdersPage() {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [error, setError] = useState('');

  const loadOrders = async (options = {}) => {
    const { withLoading = true, silent = false } = options;
    if (withLoading) {
      setLoading(true);
    }
    try {
      const filters = {};
      if (statusFilter !== 'ALL') filters.status = statusFilter;
      if (search) filters.search = search;
      const data = await adminService.getOrders(filters);
      setOrders(data);
      setError('');
    } catch (err) {
      if (!silent) {
        setOrders([]);
        setError(err.message || 'Greška pri učitavanju porudžbina');
        toast.error(err.message || 'Greška pri učitavanju porudžbina');
      }
    } finally {
      if (withLoading) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    const t = setTimeout(loadOrders, 300);
    return () => clearTimeout(t);
  }, [search, statusFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const intervalId = setInterval(() => {
      loadOrders({ withLoading: false, silent: true });
    }, 5000);

    return () => clearInterval(intervalId);
  }, [search, statusFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const loadDashboardStats = async (silent = false) => {
      try {
        const data = await adminService.getDashboard();
        setDashboard(data);
      } catch (err) {
        if (!silent) {
          toast.error(err.message || 'Greška pri učitavanju statistike.');
        }
      }
    };

    loadDashboardStats();
    const intervalId = setInterval(() => loadDashboardStats(true), 5000);
    return () => clearInterval(intervalId);
  }, []);

  const orderStats = dashboard ? [
    { label: 'Poslate', value: dashboard.shippedOrders, icon: Truck, color: 'bg-amber-50 text-amber-600' },
    { label: 'Preuzete', value: dashboard.deliveredOrders, icon: CheckCircle, color: 'bg-green-50 text-green-700' },
    { label: 'Prodatih artikala', value: dashboard.soldItems || 0, icon: Package, color: 'bg-cyan-50 text-cyan-700' },
    { label: 'Nisu preuzete', value: dashboard.notPickedUpOrders || 0, icon: AlertTriangle, color: 'bg-orange-50 text-orange-600' },
    { label: 'Odbijene', value: dashboard.rejectedOrders, icon: XCircle, color: 'bg-red-50 text-red-600' },
    { label: 'Ukupno porudžbina', value: dashboard.totalOrders || 0, icon: ShoppingBag, color: 'bg-slate-100 text-slate-700' },
    { label: 'Svi proizvodi', value: dashboard.totalProducts, icon: Package, color: 'bg-primary/10 text-primary' },
    { label: 'Mali zaliha', value: dashboard.lowStockProducts, icon: AlertTriangle, color: 'bg-orange-50 text-orange-600' },
  ] : [];

  return (
    <div>
      <PageHeader title="Porudžbine" subtitle={`Ukupno: ${orders.length}`} />

      {dashboard && (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 mb-6">
          {orderStats.map((stat) => (
            <StatCard key={stat.label} {...stat} />
          ))}
        </div>
      )}

      {/* Filters */}
      <div className="bg-card rounded-xl shadow-card border border-border p-4 mb-6 flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Pretrazi po broju, kupcu, telefonu..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="pl-9 font-ui"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-full sm:w-48 font-ui">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="ALL">Svi statusi</SelectItem>
            <SelectItem value="NEW">Nova</SelectItem>
            <SelectItem value="SHIPPED">Poslata</SelectItem>
            <SelectItem value="DELIVERED">Preuzeta</SelectItem>
            <SelectItem value="NOT_PICKED_UP">Nije preuzeta</SelectItem>
            <SelectItem value="REJECTED">Odbijena</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      {error && !loading && (
        <div className="mb-4 rounded-lg border border-border bg-card p-3">
          <p className="text-sm text-muted-foreground font-ui">{error}</p>
        </div>
      )}
      <div className="bg-card rounded-xl shadow-card border border-border overflow-hidden">
        {loading ? (
          <div className="p-6 space-y-3">
            {Array(5).fill(0).map((_, i) => <Skeleton key={i} className="h-16 rounded-lg" />)}
          </div>
        ) : orders.length === 0 ? (
          <EmptyState title="Nema porudžbina" description="Nema rezultata za odabrane filtere." icon={ShoppingBag} />
        ) : (
          <>
          <div className="md:hidden p-3 space-y-3">
            {orders.map(order => (
              <button
                key={order.id}
                type="button"
                onClick={() => navigate(`/admin/porudžbine/${order.id}`)}
                className="w-full text-left rounded-xl border border-border bg-background p-4 shadow-card"
              >
                <div className="flex items-start justify-between gap-2">
                  <p className="font-ui text-sm font-semibold text-primary">{order.orderNumber}</p>
                  <StatusBadge status={order.status} deliveryType={order.deliveryType} />
                </div>
                <p className="mt-2 text-sm font-ui font-medium text-foreground">{order.customerName}</p>
                <p className="text-xs text-muted-foreground font-ui">{order.phone}</p>
                <p className="text-xs text-muted-foreground font-ui">{order.email}</p>
                <div className="mt-3 flex items-center justify-between">
                  <p className="font-ui text-sm font-bold text-foreground">{order.total.toLocaleString('sr-RS')} RSD</p>
                  <p className="text-xs text-muted-foreground font-ui">{new Date(order.createdAt).toLocaleDateString('sr-RS')}</p>
                </div>
              </button>
            ))}
          </div>
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-muted/50 border-b border-border">
                  {['Broj', 'Kupac', 'Tel / Email', 'Status', 'Iznos', 'Datum', 'Kupac?', ''].map(h => (
                    <th key={h} className="text-left px-4 py-3 text-xs font-ui font-semibold text-muted-foreground uppercase tracking-wide">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {orders.map(order => (
                  <tr
                    key={order.id}
                    className="hover:bg-muted/30 transition-colors cursor-pointer"
                    onClick={() => navigate(`/admin/porudžbine/${order.id}`)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        navigate(`/admin/porudžbine/${order.id}`);
                      }
                    }}
                    tabIndex={0}
                    role="button"
                    aria-label={`Otvori detalje porudžbine ${order.orderNumber}`}
                  >
                    <td className="px-4 py-3">
                      <span className="font-ui text-sm font-semibold text-primary">{order.orderNumber}</span>
                    </td>
                    <td className="px-4 py-3">
                      <p className="font-ui text-sm font-medium text-foreground">{order.customerName}</p>
                    </td>
                    <td className="px-4 py-3">
                      <p className="text-sm text-foreground font-ui">{order.phone}</p>
                      <p className="text-xs text-muted-foreground font-ui">{order.email}</p>
                    </td>
                    <td className="px-4 py-3"><StatusBadge status={order.status} deliveryType={order.deliveryType} /></td>
                    <td className="px-4 py-3 font-ui font-bold text-foreground text-sm">{order.total.toLocaleString('sr-RS')} RSD</td>
                    <td className="px-4 py-3 text-xs text-muted-foreground font-ui">
                      {new Date(order.createdAt).toLocaleDateString('sr-RS')}
                    </td>
                    <td className="px-4 py-3">
                      {order.isReturningCustomer ? (
                        <span className="badge-gold text-xs flex items-center gap-1">
                          <Users size={10} />
                          {order.previousOrdersCount}x
                        </span>
                      ) : (
                        <span className="badge-green text-xs">Nov</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <Link
                        to={`/admin/porudžbine/${order.id}`}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Button variant="ghost" size="sm" className="font-ui h-8">
                          <Eye size={14} className="mr-1" /> Detalji
                        </Button>
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          </>
        )}
      </div>
    </div>
  );
}
